import { FC } from 'react';
import { Outlet, useRouter } from '@tanstack/react-router';
import { useDocumentTitle } from 'usehooks-ts';
import useApp from './hooks/use-app.hook';
import ModalRoot from './contexts/modal/modal-root';
import useFavicon from './hooks/use-favicon.hook';
import { createUseStyles } from 'react-jss';
import { Box, Stack, Theme } from '@mui/material';
import { getBreakpoint } from './theme';
import useThemeMediaQuery from './hooks/use-theme-media-query';
import useThread from './hooks/use-thread.hook';
import AppSidebar from './components/app-sidebar';
import MobileSidebar from './components/app-sidebar/mobile-sidebar';
import OrgLogo from './components/org-logo';
import Icon from './components/icon';
import Text from './components/text';
import { ThreadProvider } from './contexts/thread';
import { ThreadHistoryProvider } from './contexts/thread-history';

const useStyles = createUseStyles((theme: Theme) => ({
  content: {
    height: '100%',
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  main: {
    height: '100%',
    flex: '1 1 0%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    height: 50,
    padding: '10px 30px',
    width: '100%',
    boxSizing: 'border-box',
    gap: 4,
  },
  newChatButton: {
    backgroundColor: theme.palette.background.sidebar,
    color: theme.palette.text.primary,
    padding: '4px 6px',
    borderRadius: theme.shape.borderRadius,
    gap: 4,
    cursor: 'pointer',
    '& .label': {
      lineHeight: '100%',
    },
  },
  [getBreakpoint('md')]: {
    header: {
      padding: 10,
      height: 46,
    },
  },

  '@global': {
    'html, body, #root': {
      height: '100%',
      width: '100%',
      margin: 0,
      padding: 0,
      overflow: 'hidden',
    },
    a: {
      color: theme.palette.action.active,
      textDecoration: 'none',
      '&:hover': {
        opacity: 0.7,
      },
      '&.flat': {
        color: theme.palette.common.black,
      },
    },
    '& .notistack-SnackbarContainer': {
      '& .notistack-MuiContent.notistack-MuiContent-info': {
        backgroundColor: theme.palette.info.main,
      },
      '& .notistack-MuiContent.notistack-MuiContent-warning': {
        backgroundColor: theme.palette.warning.main,
      },
    },
  },
}));

const App: FC = () => {
  return (
    <ThreadProvider>
      <ThreadHistoryProvider>
        <AppWithContext />
      </ThreadHistoryProvider>
    </ThreadProvider>
  );
};

const AppWithContext: FC = () => {
  const {
    branding,
    pageTitle: { plain },
  } = useApp();

  const hasPageTitle = !!plain;
  const documentTitle = branding?.orgName;
  const favicon = branding?.favicon || '/favicon-blank.png';
  const router = useRouter();

  const isChat = String(router.state.location.pathname).startsWith('/chat');

  useDocumentTitle(`${documentTitle} Portal ${hasPageTitle ? ' |' : ''} ${plain}`);
  useFavicon(favicon);

  const styles = useStyles();

  const { medium } = useThemeMediaQuery();
  const { loadThread } = useThread();

  const handleNewThread = () => {
    loadThread();
  };

  return (
    <>
      <Box className={styles.content}>
        {!medium && <AppSidebar />}
        <Box component="main" className={styles.main}>
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          className={styles.header}
          width="100%"
          minWidth={0}
          flexShrink={0}
          boxSizing="border-box"
        >
            {medium && <MobileSidebar />}
            <OrgLogo />
            {medium && isChat && (
              <Stack flexDirection="row" flexGrow={1} justifyContent="flex-end">
                <Stack
                  direction="row"
                  alignItems="center"
                  className={styles.newChatButton}
                  role="button"
                  onClick={handleNewThread}
                >
                  <Icon name="plus" size="small" />
                  <Text size="small" className="label">
                    New
                  </Text>
                </Stack>
              </Stack>
            )}
          </Stack>
          <Outlet />
        </Box>
      </Box>

      <ModalRoot />
    </>
  );
};

export default App;
